import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Smartphone, Bell } from "lucide-react";
import { api } from "@/lib/auth";

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => { api.get("/notifications/prefs").then(({ data }) => setPrefs(data)).catch(() => {}); }, []);
  if (!prefs) return <p className="mt-6 text-sm text-ivory/50">Loading…</p>;

  const toggleEvent = (e) => setPrefs({ ...prefs, events: { ...prefs.events, [e]: !prefs.events[e] } });
  const toggleChannel = (c) => { if (c === "sms") return; setPrefs({ ...prefs, channels: { ...prefs.channels, [c]: !prefs.channels[c] } }); };
  const save = async () => {
    try { await api.put("/notifications/prefs", { events: prefs.events, channels: prefs.channels }); toast.success("Preferences saved"); }
    catch { toast.error("Could not save preferences"); }
  };

  return (
    <div className="mt-6 max-w-2xl" data-testid="notification-settings">
      <div className="rounded-2xl border border-gold/15 bg-obsidian-800 p-6">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Delivery Channels</p>
        <div className="mt-3 space-y-2">
          <Row testid="channel-in_app" icon={Bell} label="In-app notifications" on={prefs.channels.in_app} onClick={() => toggleChannel("in_app")} />
          <Row testid="channel-email" icon={Mail} label="Email alerts" on={prefs.channels.email} onClick={() => toggleChannel("email")} />
          <Row testid="channel-sms" icon={Smartphone} label="SMS alerts" on={false} disabled note="No SMS provider configured yet" />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-gold/15 bg-obsidian-800 p-6">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Trip Event Alerts</p>
        <div className="mt-3 space-y-2">
          {Object.entries(prefs.available_events).map(([k, label]) => (
            <Row key={k} testid={`event-${k}`} label={label} on={!!prefs.events[k]} onClick={() => toggleEvent(k)} />
          ))}
        </div>
        <p className="mt-4 text-xs text-ivory/45">Account security emails (verification, password reset, invitations) are always sent and are separate from these optional trip alerts.</p>
      </div>

      <button data-testid="save-prefs" onClick={save} className="mt-5 rounded-full bg-gold px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900 hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]">Save Preferences</button>
    </div>
  );
}

function Row({ icon: Icon, label, on, onClick, disabled, note, testid }) {
  return (
    <button data-testid={testid} onClick={disabled ? undefined : onClick} disabled={disabled}
      className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${disabled ? "cursor-not-allowed border-gold/10 opacity-60" : "border-gold/15 hover:border-gold/40"}`}>
      <span className="inline-flex items-center gap-2 text-sm text-ivory/85">{Icon && <Icon className="h-4 w-4 text-gold/70" />} {label}{note && <span className="text-xs text-ivory/40">· {note}</span>}</span>
      <span className={`relative h-5 w-9 rounded-full transition ${on ? "bg-gold" : "bg-ivory/15"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-obsidian-900 transition ${on ? "left-4" : "left-0.5"}`} />
      </span>
    </button>
  );
}
